import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ExternalLink, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Menu data
const menuData = {
  entrees: [
    { name: "Foie gras maison", description: "Chutney de figues, brioche toastée", price: "24" },
    { name: "Tartare de saumon", description: "Avocat, agrumes, wasabi", price: "22" },
    { name: "Soupe à l'oignon gratinée", description: "Croûtons, gruyère AOP", price: "16" },
    { name: "Salade de chèvre chaud", description: "Miel, noix, mesclun", price: "18" },
    { name: "Terrine de campagne", description: "Cornichons, oignons confits", price: "14" },
  ],
  plats: [
    { name: "Choucroute garnie", description: "Charcuteries artisanales, pommes de terre", price: "32" },
    { name: "Vol-au-vent aux fruits de mer", description: "Crevettes, moules, sauce crémeuse", price: "36" },
    { name: "Entrecôte grillée", description: "Sauce au poivre, frites maison", price: "42" },
    { name: "Cordon bleu de veau", description: "Jambon, gruyère, salade verte", price: "34" },
    { name: "Moules marinières", description: "Vin blanc, échalotes, frites", price: "28" },
    { name: "Cocotte de veau", description: "Légumes de saison, jus corsé", price: "38" },
    { name: "Filet de perche", description: "Beurre citronné, amandes, riz basmati", price: "36" },
  ],
  desserts: [
    { name: "Paris-Brest", description: "Crème pralinée, noisettes caramélisées", price: "14" },
    { name: "Tarte Tatin", description: "Pommes caramélisées, crème fraîche", price: "12" },
    { name: "Crème brûlée", description: "Vanille de Madagascar", price: "10" },
    { name: "Mousse au chocolat", description: "Chocolat noir 70%, chantilly", price: "11" },
    { name: "Assiette de fromages", description: "Sélection affinée, confiture", price: "16" },
  ],
  vins: [
    { name: "Chasselas AOC Genève", description: "Domaine de la Vigne Blanche", price: "38" },
    { name: "Gamay AOC Genève", description: "Cave de Genève", price: "42" },
    { name: "Pinot Noir", description: "Domaine Les Hutins", price: "48" },
    { name: "Champagne Brut", description: "Maison Billecart-Salmon", price: "75" },
  ],
};

// PDF links
const pdfLinks = {
  carte: "https://brasseriecarougeoise.ch/assets/menu/fr/Carte_des_mets_brasserie_carougeoise.pdf",
  vins: "https://brasseriecarougeoise.ch/assets/menu/fr/Carte_des_vins_brasserie_carougeoise.pdf",
  desserts: "https://brasseriecarougeoise.ch/assets/menu/fr/Carte_des_Desserts_Brasserie_carougeoise.pdf",
  menuDuJour: "https://brasseriecarougeoise.ch/assets/menu/menu_du_jour.pdf",
  petitDej: "https://brasseriecarougeoise.ch/assets/menu/fr/Carte_petit_dej_Brasserie%20Carougeoise.pdf",
  apero: "https://brasseriecarougeoise.ch/assets/menu/fr/Carte_apero_Brasserie%20Carougeoise.pdf",
};

// Menu Item Component
const MenuItem = ({ item }) => (
  <div className="flex items-baseline gap-2 py-4 border-b border-dashed border-border/60 last:border-0">
    <div className="flex-grow">
      <h4 className="font-heading text-lg text-primary">{item.name}</h4>
      <p className="text-muted-foreground text-sm font-body">{item.description}</p>
    </div>
    <div className="menu-item-line" />
    <span className="font-heading text-lg text-gold whitespace-nowrap">CHF {item.price}</span>
  </div>
);

// PDF Viewer Modal
const PDFViewerModal = ({ title, pdfUrl, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 h-full min-h-0">
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0`}
            className="w-full h-full border-0 rounded"
            title={title}
          />
        </div>
        <div className="flex justify-end pt-4">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              {t("common.open_new_tab")}
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MenuPage = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-20" data-testid="menu-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/plats/vol_au_vent.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              {t("menu.subtitle")}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-2 mb-6">
              {t("menu.title")}
            </h1>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      <section className="pt-14 md:pt-18 pb-4 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="w-20 h-px bg-gold/70 mx-auto mb-6" />

            <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto font-body leading-relaxed">
              {t("menu.description")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="pt-8 md:pt-10 pb-16 md:pb-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Tabs defaultValue="entrees" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent mb-12" data-testid="menu-tabs">
              <TabsTrigger
                value="entrees"
                className="px-6 py-3 rounded-none border border-border data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary font-heading uppercase tracking-wider"
              >
                {t("menu.entrees")}
              </TabsTrigger>
              <TabsTrigger
                value="plats"
                className="px-6 py-3 rounded-none border border-border data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary font-heading uppercase tracking-wider"
              >
                {t("menu.plats")}
              </TabsTrigger>
              <TabsTrigger
                value="desserts"
                className="px-6 py-3 rounded-none border border-border data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary font-heading uppercase tracking-wider"
              >
                {t("menu.desserts")}
              </TabsTrigger>
              <TabsTrigger
                value="vins"
                className="px-6 py-3 rounded-none border border-border data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary font-heading uppercase tracking-wider"
              >
                {t("menu.vins")}
              </TabsTrigger>
            </TabsList>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <TabsContent value="entrees" className="bg-card p-8 md:p-12 shadow-sm border border-border/40">
                <h3 className="font-heading text-3xl text-primary mb-8 text-center">{t("menu.entrees")}</h3>
                {menuData.entrees.map((item, i) => (
                  <MenuItem key={i} item={item} />
                ))}
              </TabsContent>

              <TabsContent value="plats" className="bg-card p-8 md:p-12 shadow-sm border border-border/40">
                <h3 className="font-heading text-3xl text-primary mb-8 text-center">{t("menu.plats")}</h3>
                {menuData.plats.map((item, i) => (
                  <MenuItem key={i} item={item} />
                ))}
              </TabsContent>

              <TabsContent value="desserts" className="bg-card p-8 md:p-12 shadow-sm border border-border/40">
                <h3 className="font-heading text-3xl text-primary mb-8 text-center">{t("menu.desserts")}</h3>
                {menuData.desserts.map((item, i) => (
                  <MenuItem key={i} item={item} />
                ))}
              </TabsContent>

              <TabsContent value="vins" className="bg-card p-8 md:p-12 shadow-sm border border-border/40">
                <h3 className="font-heading text-3xl text-primary mb-8 text-center">{t("menu.vins")}</h3>
                {menuData.vins.map((item, i) => (
                  <MenuItem key={i} item={item} />
                ))}
              </TabsContent>
            </motion.div>
          </Tabs>
        </div>
      </section>

      {/* Atmosphere Images */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { src: "/images/plats/dej.jpg", alt: "Petit déjeuner" },
              { src: "/images/plats/gouter.png", alt: "Goûter" },
              { src: "/images/plats/apero.jpeg", alt: "Apéritif" },
            ].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="aspect-[4/3] overflow-hidden"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover img-zoom"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
